import { useState } from "react";
import { request } from "utils/apiClient";
import { useToast } from "hooks/useToast";

interface ErrorResponse {
  name: string;
  path: string;
  message: string;
  body: {
    code: number;
    details: MessageDetails[];
    message: string;
  };
}

interface MessageDetails {
  apiErrorCode: string;
  message: string;
  stackTrace?: string;
}

interface Item {
  corpCode: string;
  corpName: string;
  stkCode: string;
  source: string;
  corpType: string;
  baseOptFlg: string;
}

export interface ImportResponse {
  validRecordCount: number;
  invalidRecordCount: number;
  registeredCorpCount: number;
  diffUpdateInfo: {
    corpCount: number;
    importType: string;
    importMessage: string;
    corpList: Item[];
  };
  allRegistInfo: {
    corpCount: number;
    importType: string;
    importMessage: string;
    corpList: Item[];
  };
}

export const useCorpImport = () => {
  const [file, setFile] = useState<File>();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadRes, setUploadRes] = useState<ImportResponse>();

  const toast = useToast();

  const readFile = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result.split(";")[1].substr(7));
        }
        reject();
      };
    });

  const handleChangeFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
    binderId: string
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setFile(files[0]);

    // ファイルをアップロードします
    toast.clearErrors();
    setIsLoading(true);
    try {
      const data = await readFile(files[0]);
      const response = await request<ImportResponse>(
        "/binder/import-bookmark/corp",
        // 新しく作成されたコレクションには BinderId がありません、空の文字列を渡すことはできません、APIはエラーを報告します
        binderId ? { importFile: data, binderId } : { importFile: data },
        "POST"
      );
      if (response) {
        setUploadRes(response);
      }
    } catch (error) {
      const e = error as ErrorResponse;
      if (e && e.body && e.body.details && e.body.details.length > 0) {
        const msgList = e.body.details.map((d: MessageDetails) => d.message);
        toast.setErrors(msgList);
      } else {
        toast.setErrors(["ファイルの読み込みに失敗しました"]);
      }
      // アップロードはページコンテンツのリセットに失敗しました
      setUploadRes(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    file,
    isLoading,
    uploadRes,
    handleChangeFile
  };
};
